import * as React from 'react';
import { useTranslation } from 'react-i18next';
import lang from '../../../common/controls/Relations/RelationsLang';
import { ResourceRelationsService, IPersonProfilesInitDataReply, ALERT } from '../../../common/services/ResourceRelationsService';
import HtmlRootControl from '../../../common/controls/Relations/HtmlRootControl';
import QdDateDisplay from '../../../common/controls/Datetime/QdDateDisplay';
import { IPersonProfileRelationData } from './PersonProfileDomainModel';
import { IConfig } from './Models';
import { OptionType } from '../../../common/controls/ResourcesAutocompleteMI';
import { IPersonProfileInsertFormRef, PersonProfileInsertForm } from './PersonProfileInsertForm';
import { IEmployeeEntity, IRelationRowTable, RequestControl, TableHeaderColumn } from '../../../common/controls/Relations/RequestControl';
import { TableDisplayCell } from '../../../common/controls/Relations/TableDisplayCell';
import { IIssue } from '../../../common/controls/Relations/IssueForRelations';
import moment from 'moment';
import EditingRowDialog from './PersonProfileRelationEditRow';
import RequiredAccessesPreview from './RequiredAccessesPreview';

export interface IPersonProfileRelationsData {
	employees: IEmployeeEntity[]
}

export interface IProps {

	onSet?: (v: IPersonProfileRelationsData) => void,

	name: string;
	value: string;
	issue: IIssue;
	isValidField: boolean,

	forAcceptant: boolean;
	subordinatesTree: boolean;

	defaultEmployee: string;

	blockInsert: boolean;
	blockEditRows: boolean,
	blockCloneRows: boolean,
	blockDeleteRows: boolean,

	blockChangeEmployees: boolean;

	config: IConfig,

	editCurrentEmployee: boolean,
}

export default function PersonProfileRelationsControl(props: IProps) {
	const { t } = useTranslation();
	const [logSystemEnabled, setLogSystemEnabled] = React.useState(false);
	const [selectedPersonProfiles, setSelectedPersonProfiles] = React.useState<OptionType[]>([]);
	const [selectedEmployees, setSelectedEmployees] = React.useState<OptionType[]>([]);
	const [editingRow, setEditingRow] = React.useState<IPersonProfileRelationData>(null);
	
	const table = React.useRef<RequestControl>();
	const rootControl = React.useRef<HTMLDivElement>();
	const insertForm = React.useRef<IPersonProfileInsertFormRef>();

	React.useEffect(() => {
		new ResourceRelationsService()
			.getPersonProfilesInitData(props.issue.id, (data) => {

				let authorizations: IPersonProfileRelationData[] = [];
				if (!props.value) {
					authorizations = parseTableData(data);
				}
				else {
					var tableResult: IPersonProfileRelationData[] = JSON.parse(props.value);
					authorizations = tableResult;
				}

				var defaultUsers: OptionType[] = [null];
				if (authorizations.length == 0) {
					defaultUsers = getDefaultUsers(data);
				}

				setLogSystemEnabled(data.logSystemEnabled);
				setSelectedEmployees(defaultUsers.filter(x => !!x));
			});
	}, []);

	React.useEffect(() => {
		HtmlRootControl(rootControl, {
			canSubmit: () => {
				if (insertForm.current && insertForm.current.hasChanges()) {
					return {
						warningMessage: t("control.relation.personProfileControlHasChanges"),
					};
				};
			}
		});
	}, []);

	function getDefaultUsers(data: IPersonProfilesInitDataReply): OptionType[] {
		let defaultUser: OptionType[] = [];
		if (props.defaultEmployee === "<employeeAssets>") {
			defaultUser = props.issue.employeeAssets.map(x => {
				return {
					value: x.id.toString(),
					label: x.label,
					kind: "Employee",
				};
			});
		}
		else if (props.defaultEmployee === "<current>" && data.currentUser) {
			defaultUser.push({
				value: data.currentUser.id.toString(),
				label: data.currentUser.name,
				kind: "Employee",
			});
		}

		if (!props.config.fields.employee.multiple) {
			return [defaultUser[0]]
		}
		if (defaultUser.length == 0) {
			return [null];
		}

		return defaultUser;
	}

	function parseTableData(dataFromApi: IPersonProfilesInitDataReply): IPersonProfileRelationData[] {
		let result: IPersonProfileRelationData[] = null;
		if (dataFromApi.value && Array.isArray(dataFromApi.value)) {
			result = dataFromApi.value.map(initItemData => {

				let item: IPersonProfileRelationData = {
					id: initItemData.id,
					employee: initItemData.employee,
					resource: initItemData.resource,
					from: initItemData.from ? moment(initItemData.from) : null,
					to: initItemData.to ? moment(initItemData.to) : null,
					deleted: initItemData.deleted,
					relationId: initItemData.relationId,
					key: null,
					errorMessages: initItemData.errorMessages,
				};

				return item;
			});

			table.current.push(result);
		}

		return result;
	}

	const fields = props.config.fields;
	return (<div className="root-relation-resource">
		<PersonProfileInsertForm
			ref={insertForm}

			selectedEmployees={selectedEmployees}
			onChangeSelectedEmployees={v => setSelectedEmployees(v)}
			employeesFilters={{
				filterId: fields.employee.filterId,
				forAcceptant: props.forAcceptant,
				subordinatesTree: props.subordinatesTree,
				maxRows: fields.employee.maxRows,
			}}

			selectedPersonProfiles={selectedPersonProfiles}
			onChangeSelectedPersonProfiles={v => setSelectedPersonProfiles(v)}
			blockInsert={props.blockInsert}
			blockChangeEmployees={props.blockChangeEmployees}

			config={props.config}

			table={table}
		/>
		<div>
			{renderTable()}
		</div>
	</div>);

	function handleEditClick(event: any, row: IPersonProfileRelationData) {
		let r = { ...row };
		setEditingRow(r);
	}

	function handleCopyClick(event: any, row: IPersonProfileRelationData) {
		let r = { ...row, copy: true };
		setEditingRow(r);
	}

	function renderTable() {
		let fields = props.config.fields;
		return <div className="authorization-relation-control" ref={rootControl}>
			<h6>
				{lang.get("applicationScope")}
			</h6>
			<RequestControl
				ref={table}
				name={props.name}
				value={props.value}

				edit={!props.blockEditRows}
				copy={!props.blockCloneRows && !props.blockInsert && !props.blockEditRows}
				remove={!props.blockDeleteRows}

				onInitData={() => { }}
				onEditClick={handleEditClick}
				onCopyClick={handleCopyClick}
				classNameRow={(row: IPersonProfileRelationData) => {
					if (row.errorMessages && row.errorMessages.length > 0)
						return "row-danger";
					return "";
				}}
				editCurrentEmployee={props.editCurrentEmployee}
				employeesFilters={{
					filterId: props.config.fields.employee.filterId,
					forAcceptant: props.forAcceptant,
					subordinatesTree: props.subordinatesTree,
					maxRows: props.config.fields.employee.maxRows,
				}}
				employeeRelationsWindow={(employe: OptionType) => {

					return null;
				}}

				changeEmployee={(rawRow: IRelationRowTable, employee: OptionType) => {
					return rawRow;
				}}

				onchangedEmployee={(rows, emp) => { }}

				onChangedEmployees={(emp) => {
					props.onSet({
						employees: emp,
					})
				}}
			>
				<TableHeaderColumn
					minWidth={200}
					render={(row: IPersonProfileRelationData) => <TableDisplayCell column="resource" errorMessages={row.errorMessages} >
						{row.resource.name}
					</TableDisplayCell>}
				>
					{lang.getDefaultByPrefixFromFn(fields.personProfile, "label", () => t("control.relation.personProfiles"))}
				</TableHeaderColumn>

				{!fields.dateFrom.columnHidden && <TableHeaderColumn
					minWidth={fields.dateFrom.columnMinWidth}
					className="table-td-data"
					render={(row: IPersonProfileRelationData) => <TableDisplayCell column="from" errorMessages={row.errorMessages} >
						<QdDateDisplay value={row.from} />
					</TableDisplayCell>}
				>
					{lang.getDefaultByPrefixFromFn(fields.dateFrom, "label", () => t("control.relation.from"))}
				</TableHeaderColumn>}
				{!fields.dateTo.columnHidden && <TableHeaderColumn
					minWidth={fields.dateTo.columnMinWidth}
					className="table-td-data"
					render={(row: IPersonProfileRelationData) => <TableDisplayCell column="to" errorMessages={row.errorMessages} >
						<QdDateDisplay value={row.to} />
					</TableDisplayCell>}
				>
					{lang.getDefaultByPrefixFromFn(fields.dateTo, "label", () => t("control.relation.to"))}
				</TableHeaderColumn>}
				<TableHeaderColumn className="table-td-data" render={(row: IPersonProfileRelationData) => <RequiredAccessesPreview data={row} />} />
			</RequestControl>
			<EditingRowDialog
				editingRow={editingRow}
				onCancel={() => setEditingRow(null)}
				onSaved={(data) => {
					let rows: IPersonProfileRelationData[] = table.current.getRows();
					for (let i = 0; i < rows.length; i++) {
						let row = rows[i];
						if (row.key == editingRow.key) {

							rows[i] = { ...rows[i], ...data, alert: ALERT.Loading };
							break;
						}
					}
					table.current.setRows(rows);

					setEditingRow(null);
				}}

				config={props.config}
			/>
		</div>
	}
}